import dayjs from 'dayjs'
import { Key, useCallback, useEffect, useState } from 'react'
import { Pressable } from '@react-aria/interactions'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { ArrowRefresh2, Edit3, Trash2 } from 'blaze-ui/icons/20'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Skeleton } from 'components/Skeleton/Skeleton'
import { CampaignOption, ReferenceMediaFile } from 'types'
import { ContentTypeSelect } from 'screens/Schedule/PostPromptsSection/ContentTypeSelect'
import {
  CONTENT_CATEGORY_KEY_TO_CONTENT_CATEGORY,
  CONTENT_CATEGORY_TO_CONTENT_CATEGORY_KEY,
  ContentCategoryKey,
} from 'screens/Schedule/PostPromptsSection/utils'
import { getMediaTypesForCategory } from 'utils/brandPlans/contentCategories'
import { ReferenceImageTrigger } from 'screens/Schedule/PostPromptsSection/ReferenceImageTrigger'
import { ReferenceImageButton } from 'screens/Schedule/PostPromptsSection/ReferenceImageButton'
import { isVideoReferenceMedia } from 'screens/Schedule/PostPromptsSection/referenceMedia'
import { ReferenceImgLoading } from 'screens/Schedule/PostPromptsSection/ReferenceImg'
import { useCreateScheduleReferenceImage } from 'hooks/queries/schedules/useCreateScheduleReferenceImage'
import { useIsStrictBrandControl } from 'hooks/useIsStrictBrandControl'
import { CONTENT_CATEGORY_CONFIG } from 'utils/brandPlans/contentCategories'
import { getDefaultTacticsForPostType } from '../CreateContentModal/utils'
import { ComposeRowData } from './Types'
import Styles from './ComposeRow.module.scss'

interface ComposeRowProps {
  row: ComposeRowData
  schedule: CampaignOption
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
  onRemove: (id: string) => void
  onRegenerate?: (id: string) => void
  canRemove: boolean
}

export const ComposeRow = ({ row, schedule, onUpdate, onRemove, onRegenerate, canRemove }: ComposeRowProps) => {
  const isStrictBrandControl = useIsStrictBrandControl()
  const tomorrow = dayjs().add(1, 'day').startOf('day')
  const disableDatesBefore = tomorrow.format('YYYY-MM-DD')
  const campaignEnd = dayjs(schedule.end_date)
  const disableDatesAfter = campaignEnd.isAfter(tomorrow) ? campaignEnd.format('YYYY-MM-DD') : undefined

  return (
    <div className={Styles.row}>
      <div className={Styles.mediaSlot}>
        {row.isBlank ? (
          <BlankMediaSlot row={row} onUpdate={onUpdate} />
        ) : (
          <AiReferenceImage row={row} schedule={schedule} onUpdate={onUpdate} />
        )}
      </div>

      <div className={Styles.body}>
        {row.isBlank ? <CaptionField row={row} onUpdate={onUpdate} /> : <TopicField row={row} onUpdate={onUpdate} />}

        <div className={Styles.controlsRow}>
          <div className={Styles.controlsLeft}>
            <ContentTypeSelect
              selectedKey={CONTENT_CATEGORY_TO_CONTENT_CATEGORY_KEY[row.contentCategory]}
              isDisabled={!row.isBlank && row.loading}
              isStrictBrandControl={isStrictBrandControl}
              variant="secondary"
              size="sm"
              onSelectionChange={(key: Key) => {
                const category = CONTENT_CATEGORY_KEY_TO_CONTENT_CATEGORY[key as ContentCategoryKey]
                const config = CONTENT_CATEGORY_CONFIG[category]
                const updates: Partial<ComposeRowData> = {
                  contentCategory: category,
                  postType: config.postType,
                  tactics: getDefaultTacticsForPostType(config.postType, schedule.tactics || []),
                }
                if (row.isBlank && row.referenceMedia) {
                  const allowed = getMediaTypesForCategory(category)
                  const currentMedia = isVideoReferenceMedia(row.referenceMedia) ? 'video' : 'image'
                  if (!allowed.includes(currentMedia)) {
                    updates.referenceMedia = null
                    updates.mediaFileIds = []
                  }
                }
                onUpdate(row.id, updates)
              }}
            />
            <Text variant="secondary" color="var(--dark-60)">
              Posting on
            </Text>
            <DateTimePicker
              dateTime={row.postAt}
              setDateTime={(dt) => {
                if (!dt) return
                onUpdate(row.id, { postAt: dt })
              }}
              disableDatesBefore={disableDatesBefore}
              disableDatesAfter={disableDatesAfter}
            >
              <Button withChevron="down" variant="secondary" size="sm">
                {row.postAt.format('MMM D, h:mma')}
              </Button>
            </DateTimePicker>
          </div>

          <div className={Styles.controlsRight}>
            {!row.isBlank && (
              <Button
                variant="tertiary"
                size="sm"
                frontIcon={ArrowRefresh2}
                aria-label="Regenerate topic"
                isDisabled={row.loading}
                onPress={() => onRegenerate?.(row.id)}
              />
            )}

            {canRemove && (
              <Button
                variant="tertiary"
                size="sm"
                frontIcon={Trash2}
                aria-label="Delete"
                onPress={() => onRemove(row.id)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface BlankMediaSlotProps {
  row: ComposeRowData
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
}

const BlankMediaSlot = ({ row, onUpdate }: BlankMediaSlotProps) => {
  const mediaTypes = getMediaTypesForCategory(row.contentCategory)

  const handleMediaSelected = (mediaFile: ReferenceMediaFile) => {
    onUpdate(row.id, { mediaFileIds: [mediaFile.id], referenceMedia: mediaFile })
  }

  if (row.referenceMedia?.url && (row.mediaFileIds?.length ?? 0) > 0) {
    return (
      <ReferenceImageTrigger onUse={handleMediaSelected} mediaTypes={mediaTypes} options={{ newStack: true }}>
        <ReferenceImageButton referenceMediaFile={row.referenceMedia} topicGenerated label="Change" />
      </ReferenceImageTrigger>
    )
  }

  return (
    <ReferenceImageTrigger onUse={handleMediaSelected} mediaTypes={mediaTypes} options={{ newStack: true }}>
      <Pressable>
        <div role="button" tabIndex={0} className={Styles.uploadSlotButton}>
          <span className={Styles.uploadCta}>Add Media</span>
        </div>
      </Pressable>
    </ReferenceImageTrigger>
  )
}

interface AiReferenceImageProps {
  row: ComposeRowData
  schedule: CampaignOption
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
}

const AiReferenceImage = ({ row, schedule, onUpdate }: AiReferenceImageProps) => {
  const { mutate: createReferenceImage, isError: errorFetchingReferenceImage } = useCreateScheduleReferenceImage({
    onMutate: () => {
      onUpdate(row.id, { referenceMedia: null })
    },
    onSuccess: (data) => {
      onUpdate(row.id, { referenceMedia: data, mediaFileIds: [data.key] })
    },
  })

  const refetchReferenceImage = useCallback(() => {
    createReferenceImage({ scheduleId: schedule.id, topic: row.topic })
  }, [createReferenceImage, row.topic, schedule.id])

  const handleChangeReferenceImage = useCallback(
    (mediaFile: ReferenceMediaFile) => {
      onUpdate(row.id, { referenceMedia: mediaFile, mediaFileIds: [mediaFile.id] })
    },
    [row.id, onUpdate],
  )

  useEffect(() => {
    if (row.loading || !row.topic || row.referenceMedia) return
    refetchReferenceImage()
  }, [row.loading, row.topic, row.referenceMedia, refetchReferenceImage])

  if (row.referenceMedia?.url) {
    return (
      <div className={Styles.imageWrapper}>
        <ReferenceImageTrigger onUse={handleChangeReferenceImage} options={{ newStack: true }}>
          <ReferenceImageButton referenceMediaFile={row.referenceMedia} topicGenerated={!!row.topic} />
        </ReferenceImageTrigger>
      </div>
    )
  }

  if (errorFetchingReferenceImage) {
    return (
      <div className={Styles.imageWrapper}>
        <Text variant="label" color="var(--dark-60)">
          Error fetching reference image
        </Text>
        <div className={Styles.retryButton}>
          <Button variant="secondary" size="sm" frontIcon={ArrowRefresh2} onPress={refetchReferenceImage}>
            Retry
          </Button>
        </div>
        <div className={Styles.referenceImageButton}>
          <ReferenceImageTrigger onUse={handleChangeReferenceImage} options={{ newStack: true }}>
            <Button variant="secondary" size="sm" frontIcon={Edit3}>
              Choose manually
            </Button>
          </ReferenceImageTrigger>
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.imageShimmerer}>
      <ReferenceImgLoading topicGenerated={!!row.topic} />
    </div>
  )
}

interface CaptionFieldProps {
  row: ComposeRowData
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
}

const CaptionField = ({ row, onUpdate }: CaptionFieldProps) => (
  <div className={Styles.captionWrapper}>
    <Text variant="label" color="var(--dark-60)">
      Caption
    </Text>
    <Input
      className={Styles.captionInput}
      placeholder="Add a caption or leave it blank to add later"
      isTextarea
      name="caption"
      value={row.topic}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
        onUpdate(row.id, { topic: e.target.value, userEdited: true })
      }
    />
  </div>
)

interface TopicFieldProps {
  row: ComposeRowData
  onUpdate: (id: string, updates: Partial<ComposeRowData>) => void
}

const TopicField = ({ row, onUpdate }: TopicFieldProps) => {
  const [isEditingTopic, setIsEditingTopic] = useState(false)

  if (row.loading) {
    return (
      <div className={Styles.topic}>
        <Skeleton active paragraph={{ rows: 3 }} title={false} className={Styles.topicSkeleton} />
      </div>
    )
  }

  return (
    <div className={Styles.topic}>
      {!isEditingTopic && (
        <Text variant="label" color="var(--dark-60)">
          Topic:
        </Text>
      )}
      <Input
        className={Styles.topicTextarea}
        placeholder="Enter a topic..."
        isTextarea
        withBorder={isEditingTopic}
        name="topic"
        value={row.topic}
        onFocus={() => setIsEditingTopic(true)}
        onBlur={() => setIsEditingTopic(false)}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onUpdate(row.id, { topic: e.target.value, userEdited: true })
        }
      />
    </div>
  )
}
